import { useCallback } from 'react';
import { useReactFlow, type XYPosition } from '@xyflow/react';
import { useCanvasFreeElementContext } from '../canvas-element-tool/CanvasFreeElementContext';
import { CanvasElementType } from '../canvas-types';
import { SECTION_MIN_WIDTH, SECTION_MIN_HEIGHT } from './section-consts';
import { colorPickerColors } from '../../common/color-picker/colorPickerColors';
import {
  getDimensions,
  getPosition,
  isSectionSupportedDropElement,
} from '../canvas-element-tool/helper';
import { getNodePositionInsideParent } from '../node-changes-event-handlers/getNodePositionInsideParent';
import {
  maybeCalculatePositionWithOffset,
  maybeSnapToGridPosition,
} from '../node-changes-event-handlers/position-events/useSectionableNodeMoveEvent';
import { RfNodeSection, SweepCanvasRfNode } from '../canvas-types/nodeTypesData';
import { useHandleDeleteSection } from './useHandleDeleteSection';
import { RFEdgeFloatingEdge } from '../edges/FloatingEdge';
import { NODE_Z_INDEX_CREATING } from '../const/zIndex';

const DEFAULT_SECTION_NAME = 'Section';

const generateNextSectionName = (nodes: SweepCanvasRfNode[]) => {
  let highestSectionNumber = 0;
  nodes.forEach((node) => {
    if (
      node.type === CanvasElementType.SECTION &&
      node.data.name.match(new RegExp(`^${DEFAULT_SECTION_NAME} \\d+$`))
    ) {
      const sectionNumber = parseInt(
        node.data.name.match(new RegExp(`^${DEFAULT_SECTION_NAME} (\\d+)$`))?.[1] || '1',
      );
      if (sectionNumber > highestSectionNumber) {
        highestSectionNumber = sectionNumber;
      }
    }
  });

  return `${DEFAULT_SECTION_NAME} ${highestSectionNumber + 1}`;
};

export const useCreateSection = () => {
  const { screenToFlowPosition, getViewport, setNodes, isNodeIntersecting } = useReactFlow<
    SweepCanvasRfNode,
    RFEdgeFloatingEdge
  >();

  const { setCreateElementActive } = useCanvasFreeElementContext();
  const { handleDeleteSection } = useHandleDeleteSection();

  const createSection = useCallback(
    (start: XYPosition, end: XYPosition) => {
      const flowPosition = screenToFlowPosition(getPosition(start, end));

      const rawDimensions = getDimensions(start, end, getViewport().zoom);

      setNodes((nodes) => {
        const newSectionID = crypto.randomUUID();

        const name = generateNextSectionName(nodes);

        const newSectionNode: RfNodeSection = {
          id: newSectionID,
          type: CanvasElementType.SECTION,
          position: {
            x: flowPosition.x,
            y: flowPosition.y,
          },
          width: Math.floor(Math.max(rawDimensions.width, SECTION_MIN_WIDTH)),
          height: Math.floor(Math.max(rawDimensions.height, SECTION_MIN_HEIGHT)),
          data: {
            color: colorPickerColors.Grey150,
            name,
            onDeleteClick: handleDeleteSection,
            readonly: false,
          },
          zIndex: NODE_Z_INDEX_CREATING,
          selected: false,
        };
        nodes.forEach((node) => {
          if (
            !node.parentId &&
            isSectionSupportedDropElement(node.type as CanvasElementType) &&
            isNodeIntersecting(node, {
              x: flowPosition.x,
              y: flowPosition.y,
              width: rawDimensions.width,
              height: rawDimensions.height,
            })
          ) {
            const nodePositionInsideParent = getNodePositionInsideParent({
              node,
              groupNode: {
                ...newSectionNode,
                measured: {
                  width: newSectionNode.width,
                  height: newSectionNode.height,
                },
              },
            });
            const newPosition = maybeSnapToGridPosition(node, nodePositionInsideParent);
            const { canvasGridPosition } = maybeCalculatePositionWithOffset(node, newPosition);

            if (!newSectionNode.data.tempChildNodes) {
              newSectionNode.data.tempChildNodes = [];
            }
            newSectionNode.data.tempChildNodes.push({
              id: node.id,
              position: canvasGridPosition,
              xyPosition: newPosition,
            });
          }
        });
        nodes.unshift(newSectionNode);
        return nodes;
      });

      setCreateElementActive(null);
    },
    [
      screenToFlowPosition,
      getViewport,
      setNodes,
      setCreateElementActive,
      handleDeleteSection,
      isNodeIntersecting,
    ],
  );

  return { createSection };
};
